// Abo selbst verwalten: Stand (Gratismonat, Kündigung, Vormerkung), Tarif
// wechseln, Vertrag kündigen. Die Regeln stehen im Backend (abo.js) — hier
// wird nur erklärt, was ein Klick bewirkt, bevor er passiert.
import { useEffect, useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import type { Tarif } from '../../../gemeinsam/lib/typen';
import { Hinweis, Karte, KartenKopf, Knopf } from '../../../gemeinsam/komponenten/Basis';
import { Fenster } from '../../../gemeinsam/komponenten/Fenster';
import { meldungZeigen } from '../../../gemeinsam/komponenten/Meldungen';

type Plan = { id: string; label: string; price: number; description: string; features: string[] };

const RANG: Record<string, number> = { starter: 1, professional: 2, enterprise: 3 };
const euro = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
/** Letzter Tag vor einem Zeitpunkt „0:00 Uhr" — so liest man Fristen. */
const letzterTag = (s: string) => new Date(Date.parse(s) - 1).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' });
const tagVon = (s: string) => new Date(s).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' });
const fuellen = (text: string, werte: Record<string, string | number>) =>
  text.replace(/\{(\w+)\}/g, (_, k: string) => String(werte[k] ?? ''));

/** Erster Tag des nächsten Monats — dann gilt eine Herabstufung. */
function naechsterMonat(): string {
  const heute = new Date();
  return new Date(heute.getFullYear(), heute.getMonth() + 1, 1).toLocaleDateString('de-DE');
}

export function AboStatus({ tarif, neuLaden }: { tarif: Tarif; neuLaden: () => Promise<void> }) {
  const { t } = useSprache();
  const { istAdmin, aktualisieren } = useAuth();
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const a = tarif.abo;

  async function tun(pfad: string, daten: object, meldung: string) {
    setLaeuft(true);
    setFehler(null);
    try {
      await api(pfad, { methode: 'POST', daten });
      await neuLaden();
      await aktualisieren();
      meldungZeigen(meldung);
    } catch (e) { setFehler(e instanceof ApiFehler ? e.message : String(e)); }
    finally { setLaeuft(false); }
  }

  const planName = (id: string | null) => (id ? id.charAt(0).toUpperCase() + id.slice(1) : '');
  return (
    <>
      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      {a.phase === 'test' && a.testBis && (
        <Hinweis art="info" titel={fuellen(t('abo.testLaeuft'), { n: a.testTageRest ?? 0, datum: letzterTag(a.testBis) })}>
          {tarif.listPrice !== null && fuellen(t('abo.testDanach'), { plan: tarif.planLabel, preis: euro(tarif.price ?? tarif.listPrice) })}
          {!tarif.sepa && !tarif.customOffer && <> {t('abo.mandatBisTestende')}</>}
        </Hinweis>
      )}
      {a.phase === 'gekuendigt' && a.kuendigungZum && (
        <Hinweis art="warnung" titel={fuellen(t('abo.gekuendigt'), { datum: letzterTag(a.kuendigungZum) })}>
          {istAdmin && (
            <Knopf className="mt-2" laedt={laeuft} onClick={() => tun('/billing/kuendigung/zuruecknehmen', {}, t('abo.zurueckMeldung'))}>
              {t('abo.zuruecknehmen')}
            </Knopf>
          )}
        </Hinweis>
      )}
      {a.naechsterPlan && a.naechsterPlanAb && (
        <Hinweis art="info" titel={fuellen(t('abo.vorgemerkt'), { plan: planName(a.naechsterPlan), datum: tagVon(a.naechsterPlanAb) })}>
          {istAdmin && (
            <Knopf className="mt-2" laedt={laeuft}
                   onClick={() => tun('/billing/plan/change', { newPlan: tarif.plan, bestaetigt: true, agb: { accepted: true, version: tarif.agbVersion } }, t('abo.vormerkungWeg'))}>
              {t('abo.vormerkungZurueck')}
            </Knopf>
          )}
        </Hinweis>
      )}
      {a.offeneAufpreise.map(x => (
        <Hinweis key={x.id} art="info">
          {fuellen(t('abo.aufpreisHinweis'), { betrag: euro(x.netto), datum: tagVon(x.datum) })}
        </Hinweis>
      ))}
    </>
  );
}

export function TarifWechsel({ tarif, neuLaden }: { tarif: Tarif; neuLaden: () => Promise<void> }) {
  const { t, sprache } = useSprache();
  const { istAdmin, aktualisieren } = useAuth();
  const [plaene, setPlaene] = useState<Plan[]>([]);
  const [ziel, setZiel] = useState<Plan | null>(null);
  const [agb, setAgb] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    api<{ plans: Plan[] }>(`/billing/plans?sprache=${sprache}`).then(r => setPlaene(r.plans)).catch(() => setPlaene([]));
  }, [sprache]);

  const a = tarif.abo;
  const gesperrt = !tarif.kannWechseln || a.phase === 'gekuendigt' || !istAdmin;
  const art = (p: Plan) => (a.imTest ? 'test' : RANG[p.id] > RANG[tarif.plan] ? 'hoch' : 'runter');

  async function wechseln() {
    if (!ziel) return;
    setLaeuft(true);
    setFehler(null);
    try {
      const r = await api<{ art: string; wirksamAb: string | null }>('/billing/plan/change', {
        methode: 'POST', daten: { newPlan: ziel.id, bestaetigt: true, agb: { accepted: true, version: tarif.agbVersion } },
      });
      await neuLaden();
      await aktualisieren();
      meldungZeigen(r.art === 'runter' && r.wirksamAb
        ? fuellen(t('abo.vorgemerktMeldung'), { plan: ziel.label, datum: tagVon(r.wirksamAb) })
        : fuellen(t('abo.gewechselt'), { plan: ziel.label }));
      setZiel(null);
    } catch (e) { setFehler(e instanceof ApiFehler ? e.message : String(e)); }
    finally { setLaeuft(false); }
  }

  return (
    <Karte>
      <KartenKopf titel={t('abo.wechselnTitel')} hinweis={t('abo.wechselnHinweis')} />
      <div className="space-y-3 p-5">
        {!istAdmin && <Hinweis art="info">{t('abo.nurAdmin')}</Hinweis>}
        {istAdmin && !tarif.kannWechseln && <Hinweis art="info">{t('abo.sonderkondition')}</Hinweis>}
        {istAdmin && tarif.kannWechseln && a.phase === 'gekuendigt' && <Hinweis art="warnung">{t('abo.erstZuruecknehmen')}</Hinweis>}
        <div className="grid gap-3 md:grid-cols-3">
          {plaene.map(p => {
            const aktuell = p.id === tarif.plan;
            return (
              <div key={p.id} className={`flex flex-col rounded-xl border p-4 ${aktuell
                ? 'border-marke-400 bg-marke-50/50 dark:border-marke-700 dark:bg-marke-950/30'
                : 'border-slate-200 dark:border-slate-800'}`}>
                <p className="font-semibold text-slate-900 dark:text-slate-100">{p.label}</p>
                <p className="text-lg font-bold text-slate-900 dark:text-slate-100">
                  {euro(p.price)}<span className="text-sm font-normal text-slate-500 dark:text-slate-400"> {t('tarif.proMonat')}</span>
                </p>
                <p className="mt-1 flex-1 text-sm text-slate-600 dark:text-slate-400">{p.description}</p>
                <Knopf className="mt-3 w-full" variante={aktuell ? 'still' : RANG[p.id] > RANG[tarif.plan] ? 'primaer' : 'sekundaer'}
                       disabled={aktuell || gesperrt} onClick={() => { setAgb(false); setFehler(null); setZiel(p); }}>
                  {aktuell ? t('abo.ihrTarif') : a.imTest ? t('abo.waehlen') : RANG[p.id] > RANG[tarif.plan] ? t('abo.hochstufen') : t('abo.runterstufen')}
                </Knopf>
              </div>
            );
          })}
        </div>
      </div>

      {ziel && (
        <Fenster titel={fuellen(t('abo.wechselTitel'), { plan: ziel.label })} beiSchliessen={() => setZiel(null)} breite="sm:max-w-lg">
          <div className="space-y-4 p-5">
            {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
            <p className="text-sm text-slate-700 dark:text-slate-300">
              {art(ziel) === 'test' && fuellen(t('abo.wechselTest'), { preis: euro(ziel.price) })}
              {art(ziel) === 'hoch' && fuellen(t('abo.wechselHoch'), { preis: euro(ziel.price) })}
              {art(ziel) === 'runter' && fuellen(t('abo.wechselRunter'), { datum: naechsterMonat(), plan: tarif.planLabel })}
            </p>
            <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input type="checkbox" className="mt-0.5" checked={agb} onChange={e => setAgb(e.target.checked)} />
              <span>
                {t('abo.agbZustimmung')}{' '}
                <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-marke-600 underline dark:text-marke-400">{t('abo.agbAnsehen')}</a>
              </span>
            </label>
            <div className="flex flex-wrap justify-end gap-2">
              <Knopf onClick={() => setZiel(null)}>{t('aktion.abbrechen')}</Knopf>
              <Knopf variante="primaer" laedt={laeuft} disabled={!agb} onClick={wechseln}>
                {art(ziel) === 'hoch' ? t('abo.kostenpflichtig') : t('abo.bestaetigen')}
              </Knopf>
            </div>
          </div>
        </Fenster>
      )}
    </Karte>
  );
}

export function Kuendigung({ tarif, neuLaden }: { tarif: Tarif; neuLaden: () => Promise<void> }) {
  const { t } = useSprache();
  const { istAdmin, aktualisieren } = useAuth();
  const [offen, setOffen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const a = tarif.abo;
  if (!istAdmin || a.phase === 'gekuendigt' || a.phase === 'beendet') return null;

  const termin = a.imTest && a.testBis ? letzterTag(a.testBis)
    : new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toLocaleDateString('de-DE');

  async function kuendigen() {
    setLaeuft(true);
    setFehler(null);
    try {
      const r = await api<{ abo: { kuendigungZum: string } }>('/billing/kuendigen', { methode: 'POST', daten: { bestaetigt: true } });
      await neuLaden();
      await aktualisieren();
      meldungZeigen(fuellen(t('abo.gekuendigtMeldung'), { datum: letzterTag(r.abo.kuendigungZum) }));
      setOffen(false);
    } catch (e) { setFehler(e instanceof ApiFehler ? e.message : String(e)); }
    finally { setLaeuft(false); }
  }

  return (
    <Karte>
      <KartenKopf titel={t('abo.kuendigenTitel')} />
      <div className="space-y-3 p-5 text-sm text-slate-700 dark:text-slate-300">
        <p>{a.imTest ? fuellen(t('abo.kuendigenTextTest'), { datum: termin }) : fuellen(t('abo.kuendigenText'), { datum: termin })}</p>
        <Knopf variante="gefahr" onClick={() => { setFehler(null); setOffen(true); }}>{t('abo.kuendigen')}</Knopf>
      </div>
      {offen && (
        <Fenster titel={t('abo.kuendigenTitel')} beiSchliessen={() => setOffen(false)} breite="sm:max-w-lg">
          <div className="space-y-4 p-5 text-sm text-slate-700 dark:text-slate-300">
            {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
            <p>{a.imTest ? fuellen(t('abo.kuendigenTextTest'), { datum: termin }) : fuellen(t('abo.kuendigenText'), { datum: termin })}</p>
            <p className="text-slate-500 dark:text-slate-400">{t('abo.aufbewahrung')}</p>
            <div className="flex flex-wrap justify-end gap-2">
              <Knopf onClick={() => setOffen(false)}>{t('aktion.abbrechen')}</Knopf>
              <Knopf variante="gefahr" laedt={laeuft} onClick={kuendigen}>{t('abo.jetztKuendigen')}</Knopf>
            </div>
          </div>
        </Fenster>
      )}
    </Karte>
  );
}
